import { analysisKeys } from '@/composables/analysis/analysis.keys';
import { competenceObservationKeys } from '@/composables/competenceObservation/competenceObservation.keys';
import { competenceTrendKeys } from '@/composables/competenceObservation/competenceTrend.keys';
import { gradeCalculationKeys } from '@/composables/gradeCalculation/gradeCalculation.keys';
import {
  invalidateObservationTimelineQueries,
  type IObservationTimelineChange,
} from '@/composables/observationTimeline/observationTimelineQueryInvalidation';
import { performanceAssessmentOverviewKeys } from '@/composables/studentOverview/performanceAssessmentOverview.keys';
import type { QueryClient } from '@tanstack/vue-query';

const PERFORMANCE_ASSESSMENT_ENTRY_ALL_KEY = ['performanceAssessmentEntries'] as const;

enum EPerformanceAssessmentEntryQueryScope {
  Count = 'count',
  FilteredCount = 'filteredCount',
  List = 'list',
}

export interface IPerformanceAssessmentEntryListKeyParams {
  schoolClassId?: number | null;
  currentPage: number;
  pageSize: number;
  schoolSubjectId?: number | null;
  searchText?: string;
  performanceAssessmentId?: number | null;
  competenceDomainId?: number | null;
  filterIncomplete?: boolean;
  studentId?: number | null;
  fromDate?: string;
  toDate?: string;
}

export interface IPerformanceAssessmentEntryCountKeyParams {
  schoolClassId?: number | null;
  schoolSubjectId?: number | null;
  searchText?: string;
  studentId?: number | null;
  competenceDomainId?: number | null;
  fromDate?: string;
  toDate?: string;
}

export interface IPerformanceAssessmentEntryFilteredCountKeyParams extends IPerformanceAssessmentEntryCountKeyParams {
  performanceAssessmentId?: number | null;
  filterIncomplete?: boolean;
}

const scopedKey = (
  key: EPerformanceAssessmentEntryQueryScope,
  schoolClassId?: number | null,
): readonly ['performanceAssessmentEntries', EPerformanceAssessmentEntryQueryScope, ...number[]] => {
  return schoolClassId == null
    ? ([...PERFORMANCE_ASSESSMENT_ENTRY_ALL_KEY, key] as const)
    : ([...PERFORMANCE_ASSESSMENT_ENTRY_ALL_KEY, key, schoolClassId] as const);
};

export const performanceAssessmentEntryKeys = {
  counts: (schoolClassId?: number | null) => scopedKey(EPerformanceAssessmentEntryQueryScope.Count, schoolClassId),
  filteredCounts: (schoolClassId?: number | null) =>
    scopedKey(EPerformanceAssessmentEntryQueryScope.FilteredCount, schoolClassId),
  lists: (schoolClassId?: number | null) => scopedKey(EPerformanceAssessmentEntryQueryScope.List, schoolClassId),

  list: (params: IPerformanceAssessmentEntryListKeyParams) =>
    [
      ...performanceAssessmentEntryKeys.lists(params.schoolClassId),
      params.currentPage,
      params.pageSize,
      params.schoolSubjectId ?? null,
      params.searchText ?? '',
      params.performanceAssessmentId ?? null,
      params.competenceDomainId ?? null,
      params.filterIncomplete ?? false,
      params.studentId ?? null,
      params.fromDate,
      params.toDate,
    ] as const,

  count: (params: IPerformanceAssessmentEntryCountKeyParams) =>
    [
      ...performanceAssessmentEntryKeys.counts(params.schoolClassId),
      params.schoolSubjectId ?? null,
      params.searchText ?? '',
      params.studentId ?? null,
      params.competenceDomainId ?? null,
      params.fromDate,
      params.toDate,
    ] as const,

  filteredCount: (params: IPerformanceAssessmentEntryFilteredCountKeyParams) =>
    [
      ...performanceAssessmentEntryKeys.filteredCounts(params.schoolClassId),
      params.schoolSubjectId ?? null,
      params.searchText ?? '',
      params.performanceAssessmentId ?? null,
      params.filterIncomplete ?? false,
      params.studentId ?? null,
      params.competenceDomainId ?? null,
      params.fromDate,
      params.toDate,
    ] as const,
};

export const invalidatePerformanceAssessmentEntryQueries = async (
  queryClient: QueryClient,
  schoolClassId?: number | null,
  timelineChange?: IObservationTimelineChange,
): Promise<void> => {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: performanceAssessmentEntryKeys.lists(schoolClassId),
      exact: false,
    }),
    queryClient.invalidateQueries({
      queryKey: performanceAssessmentEntryKeys.counts(schoolClassId),
      exact: false,
    }),
    queryClient.invalidateQueries({
      queryKey: performanceAssessmentEntryKeys.filteredCounts(schoolClassId),
      exact: false,
    }),
    queryClient.invalidateQueries({ queryKey: performanceAssessmentOverviewKeys.lists() }),
    queryClient.invalidateQueries({ queryKey: gradeCalculationKeys.all }),
    queryClient.invalidateQueries({ queryKey: competenceTrendKeys.all }),
    queryClient.invalidateQueries({ queryKey: competenceObservationKeys.lists() }),
    queryClient.invalidateQueries({ queryKey: analysisKeys.all() }),
    invalidateObservationTimelineQueries(queryClient, schoolClassId, timelineChange),
  ]);
};
